<template>
  <div
    class="choice-cards"
    :class="{ 'is-compact': compact, 'is-busy': !!loading }"
  >
    <div v-for="(group, i) in groups" :key="i" class="choice-group">
      <div v-if="group.title" class="choice-group__title">{{ group.title }}</div>
      <div
        class="choice-group__items"
        :class="{ 'is-vertical': group.vertical }"
      >
        <el-tooltip
          v-for="item in group.items"
          :key="item.type"
          :disabled="!item.disabled || !item.reason"
          :content="item.reason"
          placement="top"
        >
          <button
            type="button"
            class="choice-card"
            :class="{
              'is-selected': value === item.type,
              'is-disabled': item.disabled,
              'is-danger': item.danger,
              'is-primary': item.primary,
            }"
            @click="select(item)"
          >
            <i
              class="choice-card__icon"
              :class="loading === item.type ? 'el-icon-loading' : item.icon"
            ></i>
            <span class="choice-card__title">{{ item.title }}</span>
          </button>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "choiceCards",
  model: { prop: "value", event: "input" },
  props: {
    // [{ title?, vertical?, items: [{ type, title, icon, disabled?, reason?, danger? }] }]
    // vertical - карточки группы друг под другом, danger - красный значок (отмена),
    // primary - крупная зелёная кнопка (сохранение)
    groups: { type: Array, required: true },
    value: { type: String, default: null },
    // Низкие карточки в строку - для полосы кнопок под содержимым этапа
    compact: { type: Boolean, default: false },
    // Карточка, действие которой выполняется: у неё значок загрузки, остальные не нажимаются
    loading: { type: String, default: null },
  },
  methods: {
    // Клик по уже выбранной карточке тоже отправляет выбор: по нему мастер переходит дальше
    select(item) {
      if (item.disabled || this.loading) return;
      this.$emit("input", item.type);
    },
  },
};
</script>

<style scoped>
.choice-group + .choice-group {
  margin-top: 24px;
}
.choice-group__title {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #909399;
}
.choice-group__items {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}

.choice-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 110px;
  padding: 16px 12px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafbfc;
  color: #303133;
  font: inherit;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s,
    box-shadow 0.15s;
}
.choice-card:hover {
  background: #ecf5ff;
  border-color: #b3d8ff;
  box-shadow: 0 2px 6px rgba(64, 158, 255, 0.15);
}
.choice-card.is-selected {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}
.choice-card.is-disabled {
  background: #f5f7fa;
  border-color: #ebeef5;
  box-shadow: none;
  color: #c0c4cc;
  cursor: not-allowed;
}
.choice-card__icon {
  font-size: 28px;
  color: #409eff;
}
.choice-card.is-selected .choice-card__icon,
.choice-card.is-disabled .choice-card__icon {
  color: inherit;
}
/* Как заголовки групп («Поступление», «Расход»), цвет - по состоянию карточки */
.choice-card__title {
  font-size: 13px;
  font-weight: 600;
  text-align: center;
}

/* Компактный вид: группы в одну строку, карточки - низкие кнопки со значком слева */
.is-compact {
  display: flex;
  justify-content: center;
  gap: 12px 24px;
}
/* Группы всегда в одну строку: при нехватке места переносятся кнопки внутри группы */
/* Подпись кнопки в одну строку: группа не уже своей самой широкой кнопки */
.is-compact .choice-card__title {
  white-space: nowrap;
}
.is-compact .choice-group + .choice-group {
  position: relative;
  margin-top: 0;
}
/* Вертикальный разделитель посередине промежутка между группами - места не занимает */
.is-compact .choice-group + .choice-group::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: -12px;
  border-left: 1.5px solid #dcdfe6;
}
.is-compact .choice-group__title {
  margin-bottom: 6px;
  text-align: center;
}
/* Группы одной высоты, кнопки - посередине по высоте: если соседняя группа в два ряда */
.is-compact .choice-group {
  display: flex;
  flex-direction: column;
}
.is-compact .choice-group__items {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  justify-content: center;
  gap: 8px;
}
.is-compact .choice-card {
  flex-direction: row;
  gap: 8px;
  width: auto;
  min-height: 40px;
  padding: 8px 14px;
}
.is-compact .choice-card__icon {
  font-size: 18px;
}
/* Карточки группы друг под другом, одной ширины */
.is-compact .choice-group__items.is-vertical {
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: stretch;
}
.choice-card.is-danger .choice-card__icon {
  color: #f56c6c;
}
.choice-card.is-danger:hover {
  background: #fef0f0;
  border-color: #fbc4c4;
  box-shadow: 0 2px 6px rgba(245, 108, 108, 0.15);
}
/* Сохранение - залитая зелёная кнопка крупнее остальных, заметна среди карточек */
.choice-card.is-primary {
  background: #67c23a;
  border-color: #67c23a;
  color: #fff;
}
.choice-card.is-primary:hover {
  background: #85ce61;
  border-color: #85ce61;
  box-shadow: 0 2px 8px rgba(103, 194, 58, 0.35);
}
.choice-card.is-primary .choice-card__icon {
  color: inherit;
}
.choice-card.is-primary .choice-card__title {
  font-size: 15px;
}
.choice-card.is-primary.is-disabled {
  background: #b3e19d;
  border-color: #b3e19d;
  box-shadow: none;
}
.is-compact .choice-card.is-primary {
  min-height: 46px;
  padding: 10px 28px;
}
.is-compact .choice-card.is-primary .choice-card__icon {
  font-size: 20px;
}
.is-busy .choice-card {
  cursor: progress;
}
</style>
